import { Text } from '@shared/components';
import type { TableBodyRow } from '@shared/table-generator';

import { PermissionCheckbox } from '../../components';
import type { ActionPermissionType, AppPermissionDetails } from '../../types';

import type { PermissionListColumn } from './columns';

const ACTIONS: ActionPermissionType[] = ['view', 'update', 'create', 'status', 'delete'];

export function generateRows(data: AppPermissionDetails[], columns: PermissionListColumn[]) {
  const groupedData = data.reduce(
    (acc, row) => {
      if (row.type === 'group') acc.groups.push(row);
      if (row.type === 'header') acc.headers.push(row);
      if (row.type === 'feature') acc.permissions.push(row);
      return acc;
    },
    {
      groups: [] as AppPermissionDetails[],
      headers: [] as AppPermissionDetails[],
      permissions: [] as AppPermissionDetails[],
    },
  );

  const permissionColumns = new Set(
    columns[0].children
      .map((column) => column.type)
      .filter((type) => ACTIONS.includes(type as ActionPermissionType)),
  );

  const uniqueGroup = new Set<string>();

  return groupedData.headers.flatMap((header) => {
    const { permissions, groups } = groupedData;

    const headers = permissions.filter((permission) => permission.id.startsWith(`${header.id}.`));
    const group = groups.find((group) => header.id.split('-')[0] === `${group.id}`);

    const headerRow = generatePermissionHeaders(header, permissions, permissionColumns);
    const permissionRows = generatePermissionRows(headers, permissionColumns);

    if (group && !uniqueGroup.has(group.id)) {
      uniqueGroup.add(group.id);

      const groupRow = generateGroupRow(group, permissionColumns);
      return [groupRow, headerRow, ...permissionRows] as TableBodyRow[];
    }

    return [headerRow, ...permissionRows] as TableBodyRow[];
  });
}

function generatePermissionRows(headers: AppPermissionDetails[], permissionColumns: Set<string>) {
  const actionColumns = ACTIONS.filter((action) => permissionColumns.has(action));

  return headers.map((permission) => ({
    children: [
      {
        children: (
          <Text
            variant='body1Semibold'
            sx={(theme) => ({
              pl: theme.spacing(6),
            })}
          >
            {permission.appPermission}
          </Text>
        ),
      },
      ...actionColumns.map((action) => ({
        children: <PermissionCheckbox row={permission} action={action} />,
        align: 'center',
      })),
    ],
  }));
}

function generatePermissionHeaders(
  header: AppPermissionDetails,
  permissions: AppPermissionDetails[],
  permissionColumns: Set<string>,
) {
  const actionColumns = ACTIONS.filter((action) => permissionColumns.has(action));

  return {
    children: [
      {
        children: <Text variant='body1Semibold'>{header.appPermission}</Text>,
        sx: (theme) => ({
          fontWeight: theme.typography.fontWeightBold,
          backgroundColor: theme.palette.primary.light,
        }),
      },
      ...actionColumns.map<TableBodyRow['children'][number]>((action) => ({
        children: (
          <PermissionCheckbox
            row={header}
            action={action as ActionPermissionType}
            permissions={permissions}
            bulkActionCheckbox
          />
        ),
        align: 'center' as const,
        sx: (theme) => ({
          backgroundColor: theme.palette.primary.light,
        }),
      })),
    ],
  } as TableBodyRow;
}

function generateGroupRow(group: AppPermissionDetails, permissionColumns: Set<string>) {
  const actionColumns = ACTIONS.filter((action) => permissionColumns.has(action));

  return {
    children: [
      {
        children: <Text variant='body1Semibold'>{group.appPermission}</Text>,
        colSpan: actionColumns.length + 1,
        sx: (theme) => ({
          backgroundColor: theme.palette.primary.main,
          fontWeight: theme.typography.fontWeightBold,
        }),
      },
    ],
  } as TableBodyRow;
}
